function _extends() {
  _extends = Object.assign || function(target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
import {useTranslation} from "../../../../../__snowpack__/link/packages/i18n/dist/i18n.esm.js";
import MaterialAutocomplete from "../../../../../__snowpack__/pkg/@mui/material/Autocomplete.js";
import MaterialCircularProgress from "../../../../../__snowpack__/pkg/@mui/material/CircularProgress.js";
import MaterialTextField from "../../../../../__snowpack__/pkg/@mui/material/TextField.js";
import {Field, useField} from "../../../../../__snowpack__/pkg/formik.js";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {cmpClassName, getRandomString} from "../../../../common/generic-react.functions.js";
import FormControl from "../../form/form-control.component.js";
import {MaterialFormControlContainer} from "../select/select.styles.js";
import {makeStyles} from "../../../../../__snowpack__/pkg/@mui/styles.js";
const useStyles = makeStyles({
  filter_flex: {
    "& div>div>div.MuiButtonBase-root": {
      height: "auto",
      lineHeight: "unset",
      maxWidth: "calc(100% - 35px)",
      "& span": {
        wordBreak: "break-word",
        whiteSpace: "initial !important"
      }
    }
  }
});
function Autocomplete({
  options,
  dataCy,
  translate,
  label,
  name,
  variant = "outlined",
  autoFocus,
  autoCorrect,
  multiple,
  open = false,
  disabled = false,
  required,
  helperText,
  onChange,
  onOpen,
  onClose,
  loading,
  getOptionLabel,
  limitTags,
  renderOption,
  refetchField,
  onRefetch,
  disableCloseOnSelect,
  isOptionEqualToValue
}) {
  const {
    t
  } = useTranslation();
  const classes = useStyles();
  const [stateOpen, setStateOpen] = useState(open);
  const _dataCy = useMemo(() => dataCy || translate || label, [dataCy, label, translate]);
  const text = useMemo(() => {
    const txt = translate ? t(translate) : label;
    return typeof txt === "string" ? txt + (required ? " *" : "") : txt;
  }, [label, required, t, translate]);
  const itemName = useMemo(() => name || getRandomString(), [name]);
  const itemId = useMemo(() => `${name || "unnamed"}__${getRandomString()}`, [name]);
  const [, meta] = useField({
    name: itemName
  });
  const handleOpen = useCallback((event) => {
    setStateOpen(true);
    if (onOpen) {
      onOpen(event);
    }
  }, [onOpen]);
  const handleClose = useCallback((event, reason) => {
    setStateOpen(false);
    if (onClose) {
      onClose(event, reason);
    }
  }, [onClose]);
  const handleChange = useCallback((event, {
    value,
    reason,
    details
  }, form) => {
    if (!disabled) {
      form?.setFieldValue(itemName, value);
      if (refetchField && onRefetch) {
        refetchField.forEach((field) => {
          return form?.setFieldValue(field, void 0);
        });
        onRefetch(refetchField);
      }
      if (onChange) {
        onChange(event, value, reason, details);
      }
    }
  }, [disabled, itemName, onChange, onRefetch, refetchField]);
  const FormComponent = useCallback(({
    component
  }) => {
    return /* @__PURE__ */ React.createElement(FormControl, {
      name: itemName,
      required,
      helperText,
      variant
    }, /* @__PURE__ */ React.createElement(MaterialFormControlContainer, {
      variant,
      disabled,
      "aria-required": required
    }, component));
  }, [disabled, helperText, itemName, required, variant]);
  useEffect(() => {
    setStateOpen(open);
  }, [open]);
  const AutocompleteComponent = useCallback(({
    field,
    form
  }) => {
    const value = field.value;
    const component = /* @__PURE__ */ React.createElement(MaterialAutocomplete, {
      "data-cy": _dataCy,
      className: `${cmpClassName("select")} ${classes.filter_flex}`,
      id: itemId,
      multiple,
      limitTags,
      autoCorrect,
      autoComplete: true,
      autoHighlight: true,
      autoSelect: true,
      fullWidth: true,
      open: stateOpen,
      onOpen: handleOpen,
      onClose: handleClose,
      value,
      options,
      loading,
      getOptionLabel,
      isOptionEqualToValue,
      loadingText: "Loading...",
      noOptionsText: "No options",
      disableCloseOnSelect,
      onChange: (e, valueChange, reason, details) => handleChange(e, {
        value: valueChange,
        reason,
        details
      }, form),
      renderOption,
      renderInput: (params) => /* @__PURE__ */ React.createElement(MaterialTextField, _extends({}, params, {
        name: itemName,
        variant,
        label: text,
        required,
        autoFocus,
        error: !!(meta.touched && meta.error),
        InputProps: {
          ...params.InputProps,
          endAdornment: /* @__PURE__ */ React.createElement(React.Fragment, null, loading ? /* @__PURE__ */ React.createElement(MaterialCircularProgress, {
            color: "inherit",
            size: 20
          }) : null, params.InputProps.endAdornment)
        }
      }))
    });
    return /* @__PURE__ */ React.createElement(FormComponent, {
      component
    });
  }, [FormComponent, _dataCy, autoCorrect, autoFocus, getOptionLabel, handleChange, handleClose, handleOpen, itemId, itemName, limitTags, loading, meta.error, meta.touched, multiple, options, renderOption, required, stateOpen, text, variant, disableCloseOnSelect, isOptionEqualToValue]);
  return /* @__PURE__ */ React.createElement(Field, {
    name: itemName,
    component: AutocompleteComponent
  });
}
export default /* @__PURE__ */ React.memo(Autocomplete);
